import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createTimeOffRequest } from "../../../api/TimeTracking/timeTrackingApi";
import { ABSENCE_TYPES } from "./absencesMonthUtils";
import { ttFilterFieldSx } from "./timeTrackingStyles";

const AbsenceAssignDialog = ({
  open,
  onClose,
  onSaved,
  canRrhh,
  employees = [],
  defaultDate,
}) => {
  const theme = useTheme();
  const [userId, setUserId] = useState("");
  const [requestType, setRequestType] = useState("vacation");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReason("");
    setRequestType("vacation");
    setStartDate(defaultDate || "");
    setEndDate(defaultDate || "");
    setUserId(canRrhh && employees.length === 1 ? String(employees[0].id) : "");
  }, [open, defaultDate, canRrhh, employees]);

  const handleSave = async () => {
    if (!startDate || !endDate) {
      setError("Indica fechas de inicio y fin");
      return;
    }
    if (canRrhh && !userId) {
      setError("Selecciona el empleado");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await createTimeOffRequest({
        requestType,
        startDate,
        endDate,
        reason: reason.trim() || null,
        ...(canRrhh && userId
          ? { userId: Number(userId), assignAsApproved: true }
          : {}),
      });
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo asignar la ausencia");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {canRrhh ? "Asignar ausencia" : "Solicitar ausencia"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {canRrhh ? (
            <FormControl size="small" fullWidth sx={ttFilterFieldSx(theme)}>
              <InputLabel>Empleado</InputLabel>
              <Select
                value={userId}
                label="Empleado"
                onChange={(e) => setUserId(e.target.value)}
              >
                {employees.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          <FormControl size="small" fullWidth sx={ttFilterFieldSx}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={requestType}
              label="Tipo"
              onChange={(e) => setRequestType(e.target.value)}
            >
              {ABSENCE_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              label="Desde"
              type="date"
              size="small"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={ttFilterFieldSx(theme)}
            />
            <TextField
              label="Hasta"
              type="date"
              size="small"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={ttFilterFieldSx(theme)}
            />
          </Stack>

          <TextField
            label="Motivo"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={ttFilterFieldSx}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AbsenceAssignDialog;
